import axios from 'axios';
import mimeTypes from 'mime-types';
import { nanoid } from '@reduxjs/toolkit';
import pluralize from 'pluralize';
import { isArray, last } from 'lodash';

import {
  FileParameter,
  ResourceDownloadResponse,
} from 'api/backofficeApiClient';
import { BaseUploadFile, FileValue } from 'components/wrappers/BaseFileUpload';
import { UploadFile } from 'antd/lib/upload/interface';

const DEFAULT_DOWNLOAD_FILE_ERROR_MESSAGE =
  'Something went wrong, please try again.';

interface DownloadFileModel {
  downloadUrl: string;
  fileName?: string;
  includeFileExtension?: boolean;
  errorMessage?: string;
}

export const downloadFileByObjectURL = async (
  objectURL: string,
  fileName: string = '',
) => {
  const link = document.createElement('a');

  link.href = objectURL;
  link.download = fileName;

  // mozilla requires link to be in the dom for downloading
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadFile = async ({
  downloadUrl,
  fileName = '',
  includeFileExtension = false,
  errorMessage = DEFAULT_DOWNLOAD_FILE_ERROR_MESSAGE,
}: DownloadFileModel) => {
  try {
    const { data: fileBlob } = await axios.get<Blob>(downloadUrl, {
      responseType: 'blob',
    });

    if (includeFileExtension) {
      fileName = `${fileName}.${mimeTypes.extension(fileBlob.type)}`;
    }

    const url = URL.createObjectURL(fileBlob);

    downloadFileByObjectURL(url, fileName);
  } catch {
    throw new Error(errorMessage);
  }
};

export const generateUploadFileList = (
  files: ResourceDownloadResponse | ResourceDownloadResponse[],
): BaseUploadFile[] => {
  files = isArray(files) ? files : [files];

  return files.map((file) => ({
    uid: nanoid(),
    size: 1,
    url: '',
    type: '',
    status: 'done',
    name: file.fileName as string,
    // should contain s3Key for edit mode
    response: {
      s3Key: file.fileKey || '',
      getUrl: file.downloadUrl,
      putUrl: '',
      contentType: '',
    },
  }));
};

export const mapS3UploadFilesToDownloadResponseModel = (
  files: FileValue[],
): ResourceDownloadResponse[] =>
  files.map((file) => ({
    fileName: file.fileName,
    fileKey: file.s3Key,
    downloadUrl: '',
  }));

export const getFileNameWithoutExt = (fileName: string = '') => {
  return fileName.indexOf('.') > -1
    ? fileName.substring(0, fileName.lastIndexOf('.')).trim()
    : fileName;
};

export const getFileExt = (fileName: string = '') => {
  if (!fileName) return '';

  const fileNameArray = fileName.split('.');

  return fileNameArray[fileNameArray.length - 1];
};

export interface BaseUploadMessages {
  uploadFailed: (fileName: string) => string;
  fileSizeLimitError: (fileName: string, fileSize: number) => string;
  fileUnsupportedError: (fileName: string) => string;
  maxFileCounterError: (maxFileCount: number) => string;
  fileNameLengthError: (fileName: string, fileNameLength?: number) => string;
}

interface BaseUploadMessagesOptions {
  fileTypes: Array<string>;
}

export const getJoinedFileTypes = (fileTypes: Array<string>) => {
  if (fileTypes.length === 1) {
    return fileTypes[0];
  }

  const fileTypesCopy = [...fileTypes];
  const lastFileType = last(fileTypesCopy);
  const withoutLastFileType = fileTypesCopy.splice(0, fileTypesCopy.length - 1);

  return `${withoutLastFileType.join(', ')} and ${lastFileType}`;
};

export const getFileUploadMessages = (
  options: BaseUploadMessagesOptions,
): BaseUploadMessages => {
  const { fileTypes } = options;

  const fileTypesCount = fileTypes.length;
  const joinedFileTypes = getJoinedFileTypes(fileTypes);

  return {
    uploadFailed: (fileName: string) => `${fileName} file upload failed.`,
    fileSizeLimitError: (fileName: string, fileSize: number) =>
      `${fileName} file is too large, maximum file size is ${fileSize} MB.`,
    fileUnsupportedError: (fileName: string) =>
      `${fileName} file is not supported, acceptable file ${pluralize(
        'type',
        fileTypesCount,
      )} ${pluralize('is', fileTypesCount)} ${joinedFileTypes}.`,
    maxFileCounterError: (maxFileCount: number) =>
      `You can upload no more than ${maxFileCount} files. Please try again with the allowed quantity.`,
    fileNameLengthError: (fileName: string, fileNameLength?: number) =>
      `${fileName} is too long. Acceptable name length is ${fileNameLength} characters.`,
  };
};

export const getFileByManual = (fileList: UploadFile[]): FileParameter[] => {
  return fileList.map((file) => ({
    data: file.originFileObj,
    fileName: file.name,
  }));
};
